import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { finalize, firstValueFrom, Subject, takeUntil } from 'rxjs';
import { GetMerchantProductRequestParams, MerchantProductData } from './merchantProducts.interface';
import { Router } from '@angular/router';
import { MerchantProductsService } from '../../services/merchantProducts/merchantProducts.service';

import { getOffset, showNotification } from 'src/app/shared/helpers/helpers';
import { ModalContent } from '../../shared/modal-content';
import { ModalComponent } from 'angular-custom-modal';
import { OptionItem } from '../../shared/types/api-responses.type';
import { MerchantsService } from '../../services/merchants/merchants.service';
import { ProvidersService } from '../../services/providers/providers.service';
import { CategoriesService } from '../../services/categories/categories.service';
import { ProductsService } from '../../services/products/products.service';

@Component({
  selector: 'app-MerchantProducts',
  templateUrl: './merchantProducts.component.html',
  styleUrls: ['./merchantProducts.component.css'],
})
export class MerchantProductsComponent implements OnInit, OnDestroy {
  @ViewChild('modal') modal!: ModalComponent;

  private destroy$ = new Subject<void>();
  loading = false;
  merchants: OptionItem[] = [];
  providers: OptionItem[] = [];
  categories: OptionItem[] = [];
  products: OptionItem[] = [];

  modalData: ModalContent = new ModalContent({
    header: 'Confirmation',
    body: 'Are you sure you want to delete this merchantProduct?',
    showCloseButton: true,
    showConfirmButton: true,
    closeButtonLabel: 'Cancel',
    confirmButtonLabel: 'Delete',
    closeButtonClass: 'btn-secondary',
    confirmButtonClass: 'btn-danger',
  });

  selectedId = '';

  params = new GetMerchantProductRequestParams();
  totalRows = 0;

  cols = [
    // { field: 'id', title: 'ID' },
    { field: 'category_name', title: 'Category' },
    { field: 'merchant_name', title: 'Merchant' },
    { field: 'provider_name', title: 'Provider' },
    { field: 'product_name', title: 'Product' },
    { field: 'fee_merchant', title: 'Merchant Fee' },
    { field: 'fee_provider', title: 'Provider Fee' },
    { field: 'fee', title: 'Fee' },
    { field: 'percent_price', title: 'Percent Price (%)' },
    { field: 'action', title: 'Action', type: 'action' },
  ];

  pageSizeOptions = [5, 10, 25, 50, 100];
  rows: MerchantProductData[] = [];

  constructor(
    private cdRef: ChangeDetectorRef,
    private router: Router,
    private MerchantProductservice: MerchantProductsService,
    private readonly merchantService: MerchantsService,
    private readonly providerService: ProvidersService,
    private readonly categoryService: CategoriesService,
    private readonly productsService: ProductsService // private readonly roleService: RolesService,
  ) {}

  ngOnInit(): void {
    this.getMerchantProducts();
    this.getMerchantData();
    this.getCategoryData();
    this.getProductData();
    this.getProviderData().then();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getMerchantProducts(): void {
    this.loading = true;
    this.rows = [];

    // alert(123);
    this.MerchantProductservice.getMerchantProductService(this.params)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (response) => {
          this.rows = this.processDataForTable(response.data);
          this.totalRows = response.totalPage;
        },
        error: (err) => console.error('Error fetching merchantProduct data:', err),
      });
  }

  processDataForTable(data: MerchantProductData[]) {
    const formattedData = data.map((each, i) => ({
      ...each,
      no: getOffset(this.params.pageNo, this.params.rowPerPage) + i + 1,
    }));

    console.log('Processed Data:', formattedData); // 🔍 Debug output

    return formattedData;
  }

  changeServer(data: any): void {
    this.params.pageNo = data.current_page;
    this.params.rowPerPage = data.pagesize;
    this.params.filter = data.search;
    this.getMerchantProducts();
  }

  onInputChanged(value: string): void {
    console.log('Debounced Input:', value);
    this.params.filter = '';
    if (value) {
      this.params.filter = value;
    }
    this.cdRef.detectChanges();
  }

  goToForm(id: string): void {
    if (id === 'new') {
      this.router.navigate(['/merchant-products/new']).then();
      return;
    }
    this.router.navigate(['/merchant-products/edit', id]).then();
    return;
  }

  private async getMerchantData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.merchantService.getMerchants());
      this.merchants = data.map((merchant) => ({ label: merchant.name, value: merchant.code }));
    } catch (error) {
      showNotification('error', 'Failed to fetch merchants', JSON.stringify(error));
    }
  }

  private async getProviderData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.providerService.getProviders());
      this.providers = data.map((provider) => ({ label: provider.name, value: provider.id }));
      console.log('providerOption: ', this.providers);
    } catch (error) {
      showNotification('error', 'Failed to fetch provider', JSON.stringify(error));
    }
  }

  private async getCategoryData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.categoryService.getCategories());
      this.categories = data.map((category) => ({ label: category.name, value: category.id }));
      console.log('categoryOption: ', this.categories);
    } catch (error) {
      showNotification('error', 'Failed to fetch category', JSON.stringify(error));
    }
  }

  private async getProductData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.productsService.getProducts());
      this.products = data.map((product) => ({ label: product.name, value: product.id }));
      console.log('productOption: ', this.products);
    } catch (error) {
      showNotification('error', 'Failed to fetch products', JSON.stringify(error));
    }
  }

  onMerchantChange(merchantOption: OptionItem): void {
    this.params.merchantCode = null;
    if (merchantOption) {
      console.log('Merchant Change:', merchantOption.value);
      console.log('Merchant Change:', merchantOption.label);
      this.params.merchantCode = merchantOption.value;
    }
    this.getMerchantProducts();
  }

  onProviderChange(providerOption: OptionItem): void {
    this.params.providerId = null;
    if (providerOption) {
      console.log('Provider Change:', providerOption.value);
      console.log('Provider Change:', providerOption.label);
      this.params.providerId = providerOption.value;
    }
    this.getMerchantProducts();
  }

  onCategoryChange(categoryOption: OptionItem): void {
    this.params.categoryId = null;
    if (categoryOption) {
      console.log('category Change:', categoryOption.value);
      console.log('category Change:', categoryOption.label);
      this.params.categoryId = categoryOption.value;
    }
    this.getMerchantProducts();
  }

  onProductChange(productOption: OptionItem): void {
    this.params.productCode = null;
    if (productOption) {
      console.log('Product Change:', productOption.value);
      console.log('Product Change:', productOption.label);
      this.params.productCode = productOption.value;
    }
    this.getMerchantProducts();
  }

  // private async getRoleData(): Promise<void> {
  //   try {
  //     const { data } = await firstValueFrom(this.roleService.getRoles());
  //     this.roles = data.map(role => ({ label: role.name, value: role.id }));
  //   } catch (error) {
  //     showNotification('error', 'Failed to fetch roles', JSON.stringify(error));
  //   }
  // }

  async deleteMerchantProduct(id: string): Promise<void> {
    this.loading = true;

    try {
      const delResp = await firstValueFrom(this.MerchantProductservice.deleteMerchantProductById(id));
      showNotification('success', 'Success to delete merchantProduct', JSON.stringify(delResp));
      this.getMerchantProducts();
    } catch (error) {
      console.error('Delete MerchantProduct Error:', error);
    } finally {
      this.loading = false;
    }
  }

  async onModalDeleteConfirm(id: string): Promise<void> {
    await this.deleteMerchantProduct(id);
    this.modal.close();
  }
}
